<script setup lang="ts">
import { site } from '~/data/site'
import type { Project, Service } from '~/types/portfolio'

const portfolioStore = usePortfolioStore()
const apiOptions = {
  storage: 'local' as const,
  storageTtl: 10 * 60 * 1000,
  server: true,
  lazy: false,
  queue: false
}

type ApiResponse<T> = T | { data?: T }

function unwrap<T>(response: ApiResponse<T> | null | undefined): T | undefined {
  if (!response) return undefined
  return typeof response === 'object' && 'data' in response
    ? response.data
    : response
}

/**
 * فقط آرایه را قبول می‌کند. پاسخ‌های غیرمنتظره (آبجکت خطا، رشته، ...) نباید
 * وارد استور شوند؛ `v-for` و `.length` روی آن‌ها در رندر می‌شکنند.
 */
function asArray<T>(value: T[] | undefined): T[] | undefined {
  return Array.isArray(value) ? value : undefined
}

// Keep these SSR requests parallel so SEO content stays rendered without delaying TTFB.
const contactsRequest = useApi<ApiResponse<unknown[]>>(
  '/v1/general/contacts',
  'contacts',
  apiOptions
)
const servicesRequest = useApi<ApiResponse<Service[]>>(
  '/v1/general/services',
  'services',
  apiOptions
)
const projectsRequest = useApi<ApiResponse<Project[]>>(
  '/v1/general/projects',
  'projects',
  apiOptions
)

await Promise.all([contactsRequest, servicesRequest, projectsRequest])

const { data: contacts } = contactsRequest
const { data: services } = servicesRequest
const { data: projects } = projectsRequest

watchEffect(() => {
  const nextContacts = asArray(unwrap(contacts.value))
  const nextServices = asArray(unwrap(services.value))
  // بنر پروژه‌ها باید قبل از رسیدن به `<img :src>` رشته شود؛ مقدار خام API
  // ممکن است آبجکت رسانه یا شناسه باشد و مرورگر را دنبال آدرس بی‌معنی بفرستد.
  const nextProjects = asArray(unwrap(projects.value))?.map(normalizeProjectMedia)

  portfolioStore.$patch({
    contacts: nextContacts ?? portfolioStore.contacts,
    services: nextServices ?? portfolioStore.services,
    projects: nextProjects ?? portfolioStore.projects
  })
})

const profile = portfolioStore.profile as {
  fullname?: string
  name?: string
  job_title?: string
  role?: string
  about?: string | string[]
  // API گاهی آبجکت رسانه یا شناسه می‌فرستد، نه رشته.
  avatar?: unknown
} | null
const ownerName = profile?.fullname?.trim() || profile?.name || site.name
const jobTitle = profile?.job_title || profile?.role || ''
// `about` بسته به منبع داده رشته یا آرایه‌ای از پاراگراف‌هاست.
const aboutText = Array.isArray(profile?.about)
  ? profile.about.filter(Boolean).join(' ')
  : typeof profile?.about === 'string'
    ? profile.about
    : ''
const description = (aboutText || site.description)
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, 160)
const requestUrl = useRequestURL({ xForwardedHost: true, xForwardedProto: true })
const origin = requestUrl.hostname === 'localhost' ? site.url : requestUrl.origin
const avatar = resolveAbsoluteMediaUrl(profile?.avatar, origin)

useSeo({
  title: [ownerName, jobTitle].filter(Boolean).join(' | '),
  description,
  path: '/',
  image: avatar,
  imageAlt: ownerName,
  siteName: ownerName,
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: ownerName,
      ...(jobTitle ? { jobTitle } : {}),
      description,
      url: origin,
      ...(avatar ? { image: avatar } : {})
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: ownerName,
      url: origin,
      inLanguage: 'fa'
    }
  ]
})
</script>

<template>
  <TemplateHost view="landing" />
</template>
